import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Icon,
  alpha,
  Chip,
  Grid,
  Divider,
  useTheme,
} from "@mui/material";
import { IChartOfAccount } from "../../../../accounting.interface";
import { hasPermission } from "../../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../../auth/hooks/useAuth";

interface AccountDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  account?: IChartOfAccount;
  onEdit: () => void;
  onAddSubAccount: () => void;
  onMergeAccount?: (account: IChartOfAccount) => void;
  onSplitAccount?: (account: IChartOfAccount) => void;
  onTransferBalance?: (account: IChartOfAccount) => void;
}

const getAccountTypeColor = (accountType: string) => {
  switch (accountType) {
    case "Asset":
      return "#10B981";
    case "Liability":
      return "#EF4444";
    case "Equity":
      return "#3B82F6";
    case "Revenue":
      return "#8B5CF6";
    case "Expense":
      return "#F59E0B";
    default:
      return "#6B7280";
  }
};

const getAccountTypeIcon = (accountType: string) => {
  switch (accountType) {
    case "Asset":
      return "account_balance_wallet";
    case "Liability":
      return "credit_card";
    case "Equity":
      return "trending_up";
    case "Revenue":
      return "monetization_on";
    case "Expense":
      return "money_off";
    default:
      return "account_balance";
  }
};

export const AccountDetailsDialog: React.FC<AccountDetailsDialogProps> = ({
  open,
  onClose,
  account,
  onEdit,
  onAddSubAccount,
  onMergeAccount,
  onSplitAccount,
  onTransferBalance,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  if (!account) return null;

  const accountColor = getAccountTypeColor(account.accountType);
  const accountIcon = getAccountTypeIcon(account.accountType);
  const isInactive = account.isActive === false;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: alpha(accountColor, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: 24, color: accountColor }}>{accountIcon}</Icon>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {account.accountName}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Account Details
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={3}>
          {/* Account Code */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Account Code
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 600,
                  color: "text.primary",
                  mt: 0.5,
                }}
              >
                {account.accountCode}
              </Typography>
            </Box>
          </Grid>

          {/* Account Type */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Account Type
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={account.accountType}
                  size="small"
                  sx={{
                    bgcolor: alpha(accountColor, 0.1),
                    color: accountColor,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Currency */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Currency
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                {account.currency}
              </Typography>
            </Box>
          </Grid>

          {/* Opening Balance */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Opening Balance
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  fontFamily: "monospace",
                  mt: 0.5,
                  color:
                    account.openingBalance !== undefined
                      ? "text.primary"
                      : "text.secondary",
                }}
              >
                {account.openingBalance !== undefined
                  ? `${
                      account.currency
                    } ${account.openingBalance.toLocaleString()}`
                  : "Not set"}
              </Typography>
            </Box>
          </Grid>

          {/* Parent Account */}
          {account.parentAccountId && (
            <Grid item xs={12}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  Parent Account
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {account.parentAccountId}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Sub-Accounts Count */}
          {account.children && account.children.length > 0 && (
            <Grid item xs={12}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  Sub-Accounts
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {account.children.length} sub-account
                  {account.children.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Level */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Hierarchy Level
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                Level {account.level}
              </Typography>
            </Box>
          </Grid>

          {/* Created Date */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Created
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                {new Date(account.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{ p: 3, gap: 1, flexDirection: "column", alignItems: "stretch" }}
      >
        {/* Show inactive message if account is inactive */}
        {isInactive && (
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.error.main, 0.1),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "error.main",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 18 }}>block</Icon>
              This account is inactive and cannot be modified
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "error.dark", mt: 0.5, display: "block" }}
            >
              Inactive accounts are read-only and cannot have sub-accounts added
              or balances transferred.
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Close
          </Button>

          {/* Transfer Balance Action - Only for active leaf accounts */}
          {!isInactive &&
            account.allowDirectPosting &&
            account.currentBalance !== undefined &&
            account.currentBalance > 0 &&
            onTransferBalance && (
              <Button
                onClick={() => onTransferBalance(account)}
                variant="outlined"
                color="secondary"
                disabled={
                  !hasPermission(
                    "accounting.chart_of_accounts.transfer_balance",
                    user?.newPermissions,
                    user?.assignment_details?.id,
                    user?.is_store_owner
                  )
                }
                startIcon={<Icon>swap_horiz</Icon>}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Transfer Balance
              </Button>
            )}

          {/* Merge/Split Actions - Only for active accounts */}
          {!isInactive &&
            account.openingBalance !== undefined &&
            account.openingBalance > 0 && (
              <>
                {onMergeAccount && (
                  <Button
                    onClick={() => onMergeAccount(account)}
                    variant="outlined"
                    color="info"
                    disabled={
                      !hasPermission(
                        "accounting.chart_of_accounts.merge_accounts",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                    startIcon={<Icon>merge</Icon>}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Merge Account
                  </Button>
                )}
                {onSplitAccount && (
                  <Button
                    onClick={() => onSplitAccount(account)}
                    variant="outlined"
                    color="warning"
                    startIcon={<Icon>call_split</Icon>}
                    disabled={
                      !hasPermission(
                        "accounting.chart_of_accounts.split_accounts",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Split Account
                  </Button>
                )}
              </>
            )}

          {/* Primary Actions - Only for active accounts */}
          {!isInactive && (
            <>
              <Button
                onClick={onEdit}
                variant="outlined"
                startIcon={<Icon>edit</Icon>}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                }}
                disabled={
                  !hasPermission(
                    "accounting.chart_of_accounts.edit",
                    user?.newPermissions,
                    user?.assignment_details?.id,
                    user?.is_store_owner
                  )
                }
              >
                Edit Account
              </Button>
              <Button
                onClick={onAddSubAccount}
                disabled={
                  !hasPermission(
                    "accounting.chart_of_accounts.create",
                    user?.newPermissions,
                    user?.assignment_details?.id,
                    user?.is_store_owner
                  )
                }
                variant="contained"
                startIcon={<Icon>add</Icon>}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Add Sub-Account
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};
